import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { FormField, FormAnswer, FormFieldType } from '../models/form.model';

@Injectable({
  providedIn: 'root'
})
export class FormValidationService {

  /**
   * Validates a single form field against its validation rules
   */
  validateField(field: FormField, value: any): ValidationErrors | null {
    // Skip validation for display-only fields
    if (field.type === 'description' || field.type === 'image-display') {
      return null;
    }

    // Check required - support both field.required and field.validation.required
    const isRequired = field.required || field.validation?.required;
    if (isRequired) {
      if (this.isEmpty(value)) {
        return { required: true };
      }
    }

    if (this.isEmpty(value)) {
      return null; // Skip further validation if not required and empty
    }

    // Type-specific validation
    switch (field.type) {
      case 'text':
        return this.validateTextField(field, value);
      case 'select-one':
      case 'select-multiple':
        return this.validateSelectField(field, value);
      case 'file':
      case 'image':
        return this.validateFileField(field, value);
      default:
        return null;
    }
  }

  private validateTextField(field: FormField, value: string): ValidationErrors | null {
    const validation = field.validation;

    if (typeof value !== 'string') {
      return { invalidType: true };
    }

    if (validation?.minLength && value.length < validation.minLength) {
      return { minlength: { requiredLength: validation.minLength, actualLength: value.length } };
    }

    if (validation?.maxLength && value.length > validation.maxLength) {
      return { maxlength: { requiredLength: validation.maxLength, actualLength: value.length } };
    }

    // Check both field.pattern and field.validation.pattern for backward compatibility
    const pattern = validation?.pattern || field.pattern;
    if (pattern) {
      try {
        const regex = new RegExp(pattern);
        if (!regex.test(value)) {
          return { pattern: { requiredPattern: pattern, actualValue: value } };
        }
      } catch (err) {
        console.error('[FormValidation] Invalid regex pattern:', pattern, err);
        return { pattern: { requiredPattern: pattern, actualValue: value, error: 'Invalid pattern' } };
      }
    }

    return null;
  }

  private validateSelectField(field: FormField, value: any): ValidationErrors | null {
    const validation = field.validation;
    if (!validation) return null;

    if (field.type === 'select-multiple') {
      if (!Array.isArray(value)) {
        return { invalidType: true };
      }

      if (validation.minItems && value.length < validation.minItems) {
        return { minItems: { requiredItems: validation.minItems, actualItems: value.length } };
      }

      if (validation.maxItems && value.length > validation.maxItems) {
        return { maxItems: { requiredItems: validation.maxItems, actualItems: value.length } };
      }
    }

    return null;
  }

  private validateFileField(field: FormField, value: any): ValidationErrors | null {
    if (!value) {
      return field.required ? { required: true } : null;
    }

    // Assume value is a FileRef or File object
    // File size and type validation would happen here if needed
    return null;
  }

  /**
   * Validates entire form response before submission
   */
  validateFormResponse(fields: FormField[], answers: FormAnswer[]): ValidationErrors | null {
    const errors: any = {};
    let hasErrors = false;

    for (const field of fields) {
      const answer = answers.find(a => a.fieldId === field.id);
      const fieldError = this.validateField(field, answer?.value);
      if (fieldError) {
        errors[field.id] = fieldError;
        hasErrors = true;
      }
    }

    return hasErrors ? errors : null;
  }

  private isEmpty(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  }

  /**
   * Get user-friendly error message for validation error
   */
  getErrorMessage(field: FormField, error: ValidationErrors | null): string {
    if (!error) return '';

    if (error['required']) {
      return `${field.label} is required`;
    }
    if (error['minlength']) {
      return `${field.label} must be at least ${error['minlength'].requiredLength} characters`;
    }
    if (error['maxlength']) {
      return `${field.label} must not exceed ${error['maxlength'].requiredLength} characters`;
    }
    if (error['pattern']) {
      return `${field.label} has invalid format`;
    }
    if (error['minItems']) {
      return `Select at least ${error['minItems'].requiredItems} options`;
    }
    if (error['maxItems']) {
      return `Select at most ${error['maxItems'].requiredItems} options`;
    }
    if (error['invalidType']) {
      return `${field.label} has invalid type`;
    }

    return 'Invalid input';
  }
}
